import { _isNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import type { TeamApiId } from '@/model/Team.model';
import { TeamEventContext, type TeamEventContextType } from '@/support/teamEvents/TeamEventContext';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { type FC, type PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

export const TeamEventProvider: FC<PropsWithChildren<{ team_api_id: TeamApiId | null }>> = ({
  team_api_id,
  children,
}) => {
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [reconnectToken, setReconnectToken] = useState(0);

  // Backoff must persist across reconnect re-runs; it resets to the initial delay on a fresh open.
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  useEffect(() => {
    reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
  }, [team_api_id]);

  useEffect(() => {
    if (_isNil(team_api_id)) {
      return;
    }

    const url = new URL('/api/messaging/v1/teams/events', window.AppConfig.messagingApiUrl);
    const source = new EventSource(url.href, { withCredentials: true });

    let reconnectTimeout: ReturnType<typeof setTimeout> | undefined;

    source.onopen = () => {
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      // Close whatever we're replacing as we swap it in, so a stray previous connection can never be
      // left open even if its own teardown was missed.
      setEventSource((prev) => {
        prev?.close();
        return source;
      });
    };

    source.onerror = () => {
      // EventSource silently retries on transient blips; we disable that by closing on any error and
      // owning the backoff ourselves. A closed source won't fire onerror again, so exactly one
      // reconnect is ever scheduled per run — the single-reconnect guarantee is structural.
      source.close();
      setEventSource((prev) => {
        prev?.close();
        return null;
      });
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
      reconnectTimeout = setTimeout(() => setReconnectToken((token) => token + 1), delay);
    };

    return () => {
      clearTimeout(reconnectTimeout);
      source.close();
      setEventSource(null);
    };
  }, [team_api_id, reconnectToken]);

  const contextValue = useMemo<TeamEventContextType | null>(() => {
    if (eventSource === null) {
      return null;
    }
    return {
      subscribe: (type, listener, options) => {
        eventSource.addEventListener(
          type,
          (event: Event) => {
            try {
              listener(JSON.parse((event as MessageEvent).data));
            } catch (error) {
              ExceptionHandler.captureException(
                new InVivoError('Failed to parse team event', {
                  cause: error,
                  slug: 'team-event-parse',
                  level: 'warning',
                  context: { type },
                })
              );
            }
          },
          options
        );
      },
    };
  }, [eventSource]);

  return <TeamEventContext.Provider value={contextValue}>{children}</TeamEventContext.Provider>;
};
